import jalaali from 'jalaali-js';

export function getDaysInJalaliMonth(year: number, month: number): number {
  return jalaali.jalaaliMonthLength(year, month);
}

export function gregorianToJalali(year: number, month: number, day: number) {
  const j = jalaali.toJalaali(year, month, day);
  return { year: j.jy, month: j.jm, dat: j.jd };
}

export function jalaliToGregorian(year: number, month: number, day: number) {
  const g = jalaali.toGregorian(year, month, day);
  return { year: g.gy, month: g.gm, day: g.gd };
}

export function jalaliToDate(year: number, month: number, day: number): Date {
  return jalaali.jalaaliToDateObject(year, month, day);
}

export const weekDaysFa = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];

/** Short labels for week strip (Sat → Fri). */
export const weekDaysShortFa = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

export const jalaliMonths = [
  '',
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

/** Map JS Date.getDay() (Sun=0) to Jalali grid column (Sat=0). */
export function gregorianWeekdaysToJalali(dayIndex: number): number {
  if (dayIndex === 0) return 1;
  if (dayIndex === 1) return 2;
  if (dayIndex === 2) return 3;
  if (dayIndex === 3) return 4;
  if (dayIndex === 4) return 5;
  if (dayIndex === 5) return 6;
  return 0;
}

export function formatGregorian(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function shiftJalaliMonth(year: number, month: number, offset: number) {
  let m = month + offset;
  let y = year;
  while (m <= 0) {
    m += 12;
    y -= 1;
  }
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  return { year: y, month: m };
}

export function jalaliDateKey(year: number, month: number, day: number | string): string {
  return `${year}/${month}/${day}`;
}

export function todayJalali() {
  const now = new Date();
  return gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Add signed days to a Jalali date (via Gregorian bridge). */
export function addJalaliDays(year: number, month: number, day: number, delta: number) {
  const d = jalaliToDate(year, month, day);
  d.setDate(d.getDate() + delta);
  return gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export interface JalaliDayRef {
  year: number;
  month: number;
  day: number;
  dateKey: string;
  dowLabel: string;
  isToday: boolean;
}

/** Seven-day week starting Saturday (Persian calendar). */
export function getJalaliWeekDays(year: number, month: number, day: number): JalaliDayRef[] {
  const anchor = jalaliToDate(year, month, day);
  const offset = (anchor.getDay() + 1) % 7;
  anchor.setDate(anchor.getDate() - offset);
  const start = gregorianToJalali(anchor.getFullYear(), anchor.getMonth() + 1, anchor.getDate());
  const today = todayJalali();

  return Array.from({ length: 7 }, (_, i) => {
    const j = addJalaliDays(start.year, start.month, start.dat, i);
    return {
      year: j.year,
      month: j.month,
      day: j.dat,
      dateKey: jalaliDateKey(j.year, j.month, j.dat),
      dowLabel: weekDaysShortFa[i] ?? '',
      isToday: j.year === today.year && j.month === today.month && j.dat === today.dat,
    };
  });
}

export function parseJalaliDateKey(key: string): { year: number; month: number; day: number } | null {
  const [y, m, d] = key.split('/').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { year: y, month: m, day: d };
}

/** Convert API/storage ISO date (`YYYY-MM-DD`) to Jalali picker key (`Y/M/D`). */
export function isoToJalaliKey(iso: string): string {
  // API dates may include a time or timezone; preserve their calendar date.
  const match = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/);
  if (!match) return '';
  const [y, m, d] = match.slice(1).map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return '';
  try {
    const j = gregorianToJalali(y, m, d);
    return jalaliDateKey(j.year, j.month, j.dat);
  } catch {
    return '';
  }
}

/** Convert Jalali picker key (`Y/M/D`) to API/storage ISO date (`YYYY-MM-DD`). */
export function jalaliKeyToIso(key: string): string {
  const j = parseJalaliDateKey(key);
  if (!j) return '';
  const g = jalaliToGregorian(j.year, j.month, j.day);
  return formatGregorian(new Date(g.year, g.month - 1, g.day));
}

export function formatJalaliWeekRange(weekDays: JalaliDayRef[]): string {
  if (!weekDays.length) return '';
  const a = weekDays[0];
  const b = weekDays[weekDays.length - 1];
  return `${a.day} ${jalaliMonths[a.month] ?? ''} – ${b.day} ${jalaliMonths[b.month] ?? ''}`;
}

/** Human-readable Jalali date for display labels (`YYYY/MM/DD`). */
export function formatJalaliIsoLabel(iso: string): string {
  if (!iso) return '';
  const j = gregorianToJalali(
    Number(iso.slice(0, 4)),
    Number(iso.slice(5, 7)),
    Number(iso.slice(8, 10)),
  );
  return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.dat).padStart(2, '0')}`;
}

/** Eastern Arabic digits for Persian UI labels. */
export function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)] ?? d);
}

/** ISO-8601 week-of-year from Gregorian Y-M-D. */
export function getIsoWeekNumber(year: number, month: number, day: number): number {
  const d = new Date(year, month - 1, day);
  d.setHours(12, 0, 0, 0);
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}
